import React, { useState, useEffect } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useSelector, useDispatch } from "react-redux";
import { logout } from "../../redux/actions/authActions";

/**
 * CSATHome.jsx
 * A responsive home page aligned to the CSAT + NPS module spec (MSME-first).
 * - Hero with quick KPIs (CSAT avg, NPS)
 * - Quick Actions (Dashboard, Start Survey, Create Questionnaire, Register Company, AI Analysis, Action Plans)
 * - Survey Modes grid (Online / In-person / Telephonic / Email)
 * - Questionnaire master entry and MSME templates
 * - Gamification & Rewards area
 * - Recent low CSAT responses with quick action buttons (RCA / Action Plan)
 *
 * NOTE: Replace mock data with API/Redux wiring where indicated (TODO).
 */

/* ---------- Small UI helpers (kept inside file for simplicity) ---------- */

const StatCard = ({ label, value, delta, hint }) => (
  <div className="bg-black-800 border border-white/5 rounded-lg p-4 text-center">
    <div className="text-xs text-gray-300">{label}</div>
    <div className="text-2xl font-semibold text-yellow-200 mt-1">{value}</div>
    {typeof delta !== "undefined" && (
      <div
        className={`text-sm mt-1 ${
          delta >= 0 ? "text-green-400" : "text-red-400"
        }`}
      >
        {delta >= 0 ? `▲ ${Math.abs(delta)}% vs prev` : `▼ ${Math.abs(delta)}% vs prev`}
      </div>
    )}
    {hint && <div className="text-xs text-gray-400 mt-2">{hint}</div>}
  </div>
);

const FeatureCard = ({ title, emoji, desc, onClick }) => (
  <button
    onClick={onClick}
    className="block bg-gray-800 p-4 rounded-xl shadow hover:shadow-lg transition w-full text-left"
  >
    <div className="flex items-center gap-3 mb-3">
      <span className="text-2xl">{emoji}</span>
      <h3 className="font-semibold text-white text-lg">{title}</h3>
    </div>
    <p className="text-sm text-white">{desc}</p>
  </button>
);

const ModeCard = ({ mode, icon, desc, onCreate }) => (
  <div className="bg-gray-800 p-3 rounded-lg shadow">
    <div className="flex items-start gap-3">
      <div className="text-2xl">{icon}</div>
      <div className="flex-1">
        <div className="font-semibold text-white">{mode}</div>
        <div className="text-xs text-white">{desc}</div>
      </div>
      <div>
        <button
          onClick={() => onCreate(mode)}
          className="w-24 px-3 py-1 bg-yellow-400 text-gray-900 rounded hover:bg-yellow-500 text-sm"
        >
          Create
        </button>
      </div>
    </div>
  </div>
);

const ResponseItem = ({ r, onRCA, onActionPlan }) => (
  <div className="bg-white/5 p-3 rounded-lg flex items-start gap-3">
    <div className="w-12 flex flex-col items-center">
      <div
        className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${
          r.score <= 2 ? "bg-red-600 text-black" : r.score === 3 ? "bg-yellow-500 text-white" : "bg-green-500 text-black"
        }`}
      >
        {r.score}
      </div>
      <div className="text-xs text-white mt-1">{r.mode}</div>
    </div>
    <div className="flex-1">
      <div className="text-sm text-white font-medium">{r.customerName || "Anonymous"}</div>
      <div className="text-xs text-white">{r.comment || "— no comment —"}</div>
      <div className="text-xs text-gray-200 mt-1">{new Date(r.date).toLocaleString()}</div>
      <div className="flex gap-2 mt-2">
        <button onClick={() => onRCA(r)} className="text-xs px-2 py-1 bg-blue-600 rounded text-white">Create RCA</button>
        <button onClick={() => onActionPlan(r)} className="text-xs px-2 py-1 bg-gray-700 rounded text-white">Action Plan</button>
      </div>
    </div>
  </div>
);

/* -------------------- Main Component -------------------- */

const CSATHome = () => {
  const authState = useSelector((s) => s.authReducer);
  const user = authState?.user || null;
  const isLoggedIn = !!user;
  const navigate = useNavigate();
  const dispatch = useDispatch();

  // UI state
  const [mobileOpen, setMobileOpen] = useState(false);
  const [userName, setUserName] = useState(user?.name || "");
  const [modal, setModal] = useState({ show: false, type: null, data: {} });
  // Replace these with real analytics from store / API
  const [kpis, setKpis] = useState({
    csatAvg: 4.1,
    csatDelta: 3.2, // percent vs previous
    nps: 32,
    responseRate: 18,
  });

  // Recent low csat sample (in production fetch from /responses?filter=low)
  const [recentLowResponses, setRecentLowResponses] = useState([
    {
      id: "r1",
      score: 1,
      comment: "Wait time was too long; staff absent.",
      customerName: "Ramesh",
      mode: "In-person",
      date: Date.now() - 1000 * 60 * 60 * 24,
    },
    {
      id: "r2",
      score: 2,
      comment: "Product quality not as expected.",
      customerName: "Meena",
      mode: "Online",
      date: Date.now() - 1000 * 60 * 60 * 36,
    },
  ]);

  useEffect(() => {
    setUserName(user?.name || "");
    // TODO: dispatch action to load KPI & recent responses if not present in Redux
  }, [user]);

  const closeModal = () => {
    setModal({ show: false, type: null, data: {} });
  };

  const getDummyContent = (type, data) => {
    switch (type) {
      case 'dashboard':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Dashboard</h3>
            <p className="mb-2">Average CSAT: 4.1/5</p>
            <p className="mb-2">NPS: 32</p>
            <p>Response Rate: 18%</p>
            <ul className="mt-4 space-y-1 text-sm">
              <li>Recent Survey: Online Survey - 50 responses</li>
              <li>Top Issue: Wait times</li>
            </ul>
          </div>
        );
      case 'companies':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Companies</h3>
            <ul className="space-y-2">
              <li>Kirana Store - Active</li>
              <li>Sample Salon - Pending</li>
            </ul>
          </div>
        );
      case 'aiAnalysis':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">AI Analysis</h3>
            <p className="mb-2">AI Output: Common theme detected - "Service delays" in 40% of responses.</p>
            <p>Suggested Action: Train staff on peak hours.</p>
          </div>
        );
      case 'actionPlans':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Action Plans</h3>
            <ul className="space-y-2">
              <li>Plan 1: Reduce wait time - Due: 2025-11-01</li>
              <li>Plan 2: Quality check - Owner: Ramesh</li>
            </ul>
          </div>
        );
      case 'profile':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Profile</h3>
            <p>Name: {userName || 'User'}</p>
            <p>Email: dummy@example.com</p>
            <p>Role: MSME Owner</p>
          </div>
        );
      case 'login':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Login</h3>
            <div className="space-y-3">
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Email" />
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Password" type="password" />
              <button onClick={closeModal} className="w-full bg-blue-600 p-2 rounded">Login</button>
            </div>
          </div>
        );
      case 'signup':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-4">Register</h3>
            <div className="space-y-3">
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Name" />
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Email" />
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Password" type="password" />
              <button onClick={closeModal} className="w-full bg-green-600 p-2 rounded">Signup</button>
            </div>
          </div>
        );
      case 'createSurvey':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Create Survey</h3>
            <div className="space-y-3">
              <input className="w-full p-2 bg-gray-700 rounded text-white" defaultValue="Survey Title" />
              <textarea className="w-full p-2 bg-gray-700 rounded text-white" defaultValue="Description" />
              <button onClick={closeModal} className="w-full bg-yellow-600 p-2 rounded">Create</button>
            </div>
          </div>
        );
      case 'createQuestionnaire':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Create Questionnaire</h3>
            <div className="space-y-3">
              <input className="w-full p-2 bg-gray-700 rounded text-white" defaultValue="Questionnaire" />
              <button onClick={closeModal} className="w-full bg-yellow-600 p-2 rounded">Create</button>
            </div>
          </div>
        );
      case 'registerCompany':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Register Company</h3>
            <div className="space-y-3">
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Company Name" defaultValue="MSME" />
              <input className="w-full p-2 bg-gray-700 rounded text-white" placeholder="Industry" defaultValue="Retail" />
              <button onClick={closeModal} className="w-full bg-yellow-600 p-2 rounded">Register</button>
            </div>
          </div>
        );
      case 'runAI':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Export & Run AI</h3>
            <p>AI Run: Analysis complete. Key insight: Improve staff training.</p>
            <button onClick={closeModal} className="mt-4 bg-blue-600 p-2 rounded">Export</button>
          </div>
        );
      case 'surveys':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Surveys</h3>
            <ul className="space-y-2">
              <li>Online Survey - 100 responses</li>
              <li>In-person - 50 responses</li>
            </ul>
          </div>
        );
      case 'questionnaires':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Questionnaires</h3>
            <ul className="space-y-2">
              <li>Basic CSAT Template</li>
              <li>MSME Feedback Form</li>
            </ul>
          </div>
        );
      case 'rewards':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Manage Rewards</h3>
            <p>Rewards: 100 points per response.</p>
            <button onClick={closeModal} className="mt-4 bg-yellow-600 p-2 rounded">Save</button>
          </div>
        );
      case 'rca':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Create RCA for {data.response?.customerName}</h3>
            <p>Comment: {data.response?.comment}</p>
            <div className="mt-4 space-y-2">
              <p>1. Why? reason 1</p>
              <p>2. Why? reason 2</p>
              <button onClick={closeModal} className="bg-blue-600 p-2 rounded">Save RCA</button>
            </div>
          </div>
        );
      case 'actionPlan':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Action Plan for {data.response?.customerName}</h3>
            <p>Score: {data.response?.score}</p>
            <div className="mt-4 space-y-2">
              <p>Action: Train staff - Due: 2025-11-01</p>
              <button onClick={closeModal} className="bg-gray-700 p-2 rounded">Save Plan</button>
            </div>
          </div>
        );
      case 'cloneTemplate':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Clone Template: {data.template}</h3>
            <p>Cloned template with 3 questions.</p>
            <button onClick={closeModal} className="mt-4 bg-yellow-600 p-2 rounded">Use</button>
          </div>
        );
      case 'templates':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Browse Templates</h3>
            <ul className="space-y-2">
              <li>Kirana Store Template</li>
              <li>Salon Feedback</li>
            </ul>
          </div>
        );
      case 'reports':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Reports</h3>
            <p>Report: Monthly CSAT Summary</p>
            <ul className="mt-4 space-y-1">
              <li>Oct 2025: 4.1 avg</li>
            </ul>
          </div>
        );
      case 'settings':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">CSAT Settings</h3>
            <p>Enable Rewards: On</p>
            <p>Default Mode: Online</p>
          </div>
        );
      case 'contact':
        return (
          <div>
            <h3 className="text-lg font-semibold mb-2">Talk to Sales</h3>
            <p>Contact: sales@think5whys.com</p>
            <button onClick={closeModal} className="mt-4 bg-blue-600 p-2 rounded">Send Message</button>
          </div>
        );
      case 'privacy':
        return <div><h3 className="text-lg font-semibold mb-2">Privacy Policy</h3><p>Privacy text: We protect your data.</p></div>;
      case 'terms':
        return <div><h3 className="text-lg font-semibold mb-2">Terms of Use</h3><p>Terms: Standard usage agreement.</p></div>;
      case 'help':
        return <div><h3 className="text-lg font-semibold mb-2">Help</h3><p>Help: Contact support.</p></div>;
      default:
        return <p>Content for {type}</p>;
    }
  };

  const handleLogout = () => {
    dispatch(logout());
    navigate("/csat");
  };

  const handleCreateSurvey = (mode) => {
    setModal({ show: true, type: 'createSurvey', data: { mode } });
  };

  const handleCreateQuestionnaire = () => {
    setModal({ show: true, type: 'createQuestionnaire' });
  };

  const handleRegisterCompany = () => {
    setModal({ show: true, type: 'registerCompany' });
  };

  const handleRunAI = () => {
    setModal({ show: true, type: 'runAI' });
  };

  const handleRCA = (response) => {
    setModal({ show: true, type: 'rca', data: { response } });
  };

  const handleActionPlan = (response) => {
    setModal({ show: true, type: 'actionPlan', data: { response } });
  };

  return (
    <div className="min-h-screen bg-gray-500 font-sans text-gray-900">
      {/* Header */}
      <header className="bg-black shadow-sm sticky top-0 z-40">
        <div className="mx-auto px-4 sm:px-6">
          <div className="flex items-center justify-between h-16">
            <div className="flex items-start gap-4">
              <button
                className="text-yellow-200 bg-black font-extrabold text-xl text-start"
                aria-label="CSAT Home"
                onClick={() => navigate("/csat")}
              >
                Think5Whys CSAT  
              </button>
              <nav className="hidden md:flex space-x-3 text-sm">
                <button onClick={() => setModal({ show: true, type: 'dashboard' })} className="px-3 py-2 rounded hover:bg-blue-600">Dashboard</button>
                <button onClick={() => setModal({ show: true, type: 'surveys' })} className="px-3 py-2 rounded hover:bg-blue-600">Surveys</button>
                <button onClick={() => setModal({ show: true, type: 'questionnaires' })} className="px-3 py-2 rounded hover:bg-blue-600">Questionnaires</button>
                <button onClick={() => setModal({ show: true, type: 'companies' })} className="px-3 py-2 rounded hover:bg-blue-600">Companies</button>
                <button onClick={() => setModal({ show: true, type: 'aiAnalysis' })} className="px-3 py-2 rounded hover:bg-blue-600">AI Analysis</button>
                <button onClick={() => setModal({ show: true, type: 'actionPlans' })} className="px-3 py-2 rounded hover:bg-blue-600">Action Plans</button>
              </nav>
            </div>

            <div className="flex flex-col items-end gap-0">
              <div className="hidden md:flex items-end gap-3">
                {isLoggedIn ? (
                  <>
                    <button onClick={() => setModal({ show: true, type: 'profile' })} className="px-3 py-1 rounded border border-gray-200 text-sm">Profile</button>
                    <button onClick={() => setModal({ show: true, type: 'dashboard' })} className="px-3 py-1 rounded bg-yellow-600 text-sm">Dashboard</button>
                    <button onClick={handleLogout} className="px-3 py-1 rounded bg-red-500 text-white text-sm">Logout</button>
                  </>
                ) : (
                  <>
                    <button onClick={() => setModal({ show: true, type: 'login' })} className="px-3 py-1 rounded border border-gray-200 text-sm">Login</button>
                    <button onClick={() => setModal({ show: true, type: 'signup' })} className="px-3 py-1 rounded bg-gray-800 text-white text-sm">Register</button>
                  </>
                )}
              </div>
              <div className="text-sm text-white">Hi, <span className="font-semibold">{userName}</span></div>


              {/* Mobile menu toggle */}
              <div className="md:hidden">
                <button
                  onClick={() => setMobileOpen((p) => !p)}
                  aria-label="Toggle menu"
                  className="p-2 rounded-md bg-blue-600 w-20"
                >
                  {mobileOpen ? "✕" : "☰"}
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Mobile nav */}
        {mobileOpen && (
          <div className="md:hidden bg-black border-t">
            <div className="px-4 py-3 space-y-1">
              <button onClick={() => setModal({ show: true, type: 'dashboard' })} className="block px-2 py-2 rounded hover:bg-blue-600">Dashboard</button>
              <button onClick={() => setModal({ show: true, type: 'companies' })} className="block px-2 py-2 rounded hover:bg-blue-600">Companies</button>
              <button onClick={() => setModal({ show: true, type: 'aiAnalysis' })} className="block px-2 py-2 rounded hover:bg-blue-600">AI Analysis</button>
              <button onClick={() => setModal({ show: true, type: 'actionPlans' })} className="block px-2 py-2 rounded hover:bg-blue-600">Action Plans</button>
            </div>
          </div>
        )}
      </header>

      <main className="mx-auto px-4 sm:px-2 lg:px-2 py-2 flex flex-col items-center">
        {/* Hero + KPIs */}
        <section className="grid grid-cols-1 lg:grid-cols-3 justify-center gap-6 place-items-center">
          <div className="flex-1 justify-center lg:col-span-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg p-6 shadow-sm">
            <h1 className="text-m font-bold text-yellow-300">CSAT & NPS for Micro-Enterprises</h1>
            <p className="flex flex-row text-center text-xs text-gray-200 mt-2">
              Simple surveys, multi-mode capture, AI-assisted insights, RCA-driven action plans and gamified respondent rewards — built to scale for MSMEs.
            </p>

            <div className="mt-6 grid grid-cols-2 sm:grid-cols-2 md:grid-cols-4 gap-1 w-full max-w-4xl mx-auto place-items-center">
              <button
                onClick={() => handleCreateSurvey("Online")}
                className="flex flex-col items-center justify-center px-4 py-3 bg-yellow-600 text-black font-semibold rounded-lg shadow hover:bg-yellow-500 transition"
              >
                <span className="text-lg">⚡</span>
                <span className="mt-1 text-sm sm:text-base">Start Quick Survey</span>
              </button>

              <button
                onClick={handleCreateQuestionnaire}
                className="flex flex-col items-center justify-center px-4 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition"
              >
                <span className="text-lg">🧩</span>
                <span className="mt-1 text-sm sm:text-base">Create Questionnaire</span>
              </button>

              <button
                onClick={handleRegisterCompany}
                className="flex flex-col items-center justify-center px-4 py-3 border border-gray-600 text-white rounded-lg hover:bg-gray-800 transition"
              >
                <span className="text-lg">🏢</span>
                <span className="mt-1 text-sm sm:text-base">Register Company</span>
              </button>

              <button
                onClick={handleRunAI}
                className="flex flex-col items-center justify-center px-4 py-3 bg-blue-600 text-white font-semibold rounded-lg shadow hover:bg-blue-700 transition"
              >
                <span className="text-lg">🤖</span>
                <span className="mt-1 text-sm sm:text-base">Export & Run AI</span>
              </button>
            </div>
            <div className="mt-6 grid grid-cols-1 sm:grid-cols-3 gap-3">
              <StatCard label="Avg CSAT" value={`${kpis.csatAvg.toFixed(2)} / 5`} delta={kpis.csatDelta} hint="Primary CSAT question average" />
              <StatCard label="NPS" value={`${kpis.nps}`} delta={1} hint="Promoters − Detractors" />
              <StatCard label="Response Rate" value={`${kpis.responseRate}%`} hint="Responses / invites" />
            </div>
          </div>

          {/* Sidebar: quick summary and gamification */}
          <aside className="bg-black rounded-lg p-4 shadow">
            <div className="flex items-center justify-between">
              <div>
                <div className="text-xs text-yellow-200">Leaderboard</div>
                <div className="text-white text-lg font-semibold mt-1">Top MSME Today</div>
              </div>
              <div className="text-right">
                <div className="text-sm text-white">Pts</div>
                <div className="font-bold text-yellow-500">1,350</div>
              </div>
            </div>

            <div className="mt-4 space-y-2">
              <div className="text-xs text-white">You can enable rewards per survey on the Survey settings page.</div>
              <button onClick={() => setModal({ show: true, type: 'rewards' })} className="block text-center mt-3 px-3 py-2 bg-yellow-600 rounded hover:bg-yellow-500 text-white font-medium">Manage Rewards</button>
            </div>
          </aside>
        </section>

        {/* Quick Actions & Modes */}
        <section className="mt-2 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 grid grid-cols-1 sm:grid-cols-2 gap-4">
            <FeatureCard
              title="Surveys"
              emoji="📝"
              desc="Create & capture surveys across Online, In-person, Telephonic and Email channels."
              onClick={() => setModal({ show: true, type: 'surveys' })}
            />
            <FeatureCard
              title="Questionnaire Master"
              emoji="📋"
              desc="Build templates, version and reuse for multiple MSME verticals."
              onClick={() => setModal({ show: true, type: 'questionnaires' })}
            />
            <FeatureCard
              title="AI Analysis"
              emoji="🤖"
              desc="Export responses, run LLM prompts, save versioned analysis and convert to RCA/Action Plans."
              onClick={() => setModal({ show: true, type: 'aiAnalysis' })}
            />
            <FeatureCard
              title="Action Plans & RCA"
              emoji="🔧"
              desc="Convert low CSAT responses into 5-Whys or Fishbone RCA and create measurable action plans."
              onClick={() => setModal({ show: true, type: 'actionPlans' })}
            />
          </div>

          {/* Survey modes quick create */}
          <div className="space-y-4">
            <div className="text-lg font-semibold text-yellow-200 mb-2">Quick Survey Modes</div>
            <ModeCard mode="Online" icon="🌐" desc="Link, QR or embedded form. Best for smartphone users." onCreate={handleCreateSurvey} />
            <ModeCard mode="In-person" icon="📱" desc="Kiosk/tablet or offline capture with sync." onCreate={handleCreateSurvey} />
            <ModeCard mode="Telephonic" icon="📞" desc="Surveyor manual entry during calls (telecaller flow)." onCreate={handleCreateSurvey} />
            <ModeCard mode="Email" icon="✉️" desc="Tokenized links for targeted email invites." onCreate={handleCreateSurvey} />
          </div>
        </section>

        {/* MSME Templates */}
        <section className="mt-2 bg-black rounded-lg p-4 shadow">
          <h2 className="text-lg font-semibold text-yellow-200">MSME Micro-Templates</h2>
          <p className="text-sm text-white mt-1">Starter templates specially tuned for micro enterprises — quick 2–4 question surveys.</p>
          <div className="mt-4 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-4 gap-3 text-white">
            {[
              { key: "kirana", label: "Kirana Store" },
              { key: "salon", label: "Salon" },
              { key: "mechanic", label: "Mechanic / Repair" },
              { key: "eatery", label: "Local Eatery" },
              { key: "tuition", label: "Tuition / Coaching" },
            ].map((t) => (
              <div key={t.key} className="p-3 rounded border border-gray-100 flex items-center justify-between">
                <div>
                  <div className="font-medium text-blue-500">{t.label}</div>
                  <div className="text-xs text-white">2–3 Qs • Quick feedback</div>
                </div>
                <div>
                  <button
                    onClick={() => setModal({ show: true, type: 'cloneTemplate', data: { template: t.key } })}
                    className="w-24 px-3 py-1 bg-yellow-400 text-black rounded text-sm"
                  >
                    Use
                  </button>
                </div>
              </div>
            ))}
          </div>
        </section>

        {/* Recent low CSAT responses & actions */}
        <section className="mt-0 grid grid-cols-1 lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2 bg-black rounded-lg p-4 shadow">
            <h3 className="text-yellow-200 font-semibold">Insights & Recommended Actions</h3>
            <p className="text-xs text-white mt-1">AI-suggested themes, top issues and one-click action creation (link to AI page).</p>

            <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-3">
              <div className="bg-gray-500 p-3 rounded">
                <div className="text-sm font-semibold text-yellow-200">Top Negative Themes</div>
                <ul className="mt-2 text-xs text-white list-disc list-inside">
                  <li>Wait time / staff availability (32 mentions)</li>
                  <li>Product quality concerns (19 mentions)</li>
                  <li>Delivery issues (11 mentions)</li>
                </ul>
                <div className="mt-3">
                  <button onClick={() => setModal({ show: true, type: 'aiAnalysis' })} className="text-xs text-yellow-200 hover:underline">Run full AI analysis →</button>
                </div>
              </div>

              <div className="bg-gray-500 p-3 rounded">
                <div className="text-sm text-yellow-200 font-semibold">Low Score Responses (recent)</div>
                <div className="mt-2 space-y-2">
                  {recentLowResponses.map((r) => (
                    <ResponseItem key={r.id} r={r} onRCA={handleRCA} onActionPlan={handleActionPlan} />
                  ))}
                </div>
              </div>
            </div>
          </div>

          <aside className="bg-black rounded-lg p-4 shadow">
            <h4 className="text-yellow-200 font-semibold">Quick Links</h4>
            <div className="mt-3 flex flex-col gap-2">
              <button onClick={() => setModal({ show: true, type: 'createSurvey' })} className="px-3 py-2 rounded border text-sm bg-blue-700 text-white">Start New Survey</button>
              <button onClick={() => setModal({ show: true, type: 'templates' })} className="px-3 py-2 rounded border text-sm bg-blue-700 text-white">Browse Templates</button>
              <button onClick={() => setModal({ show: true, type: 'reports' })} className="px-3 py-2 rounded border text-sm bg-blue-700 text-white">View Reports</button>
              <button onClick={() => setModal({ show: true, type: 'settings' })} className="px-3 py-2 rounded border text-sm bg-blue-700 text-white">CSAT Settings</button>
            </div>
          </aside>
        </section>

        {/* Footer CTA */}
        <section className="mt-2 bg-gradient-to-r from-gray-900 to-gray-800 text-white rounded-lg p-6 text-center">
          <h3 className="text-lg font-semibold">Ready to scale CSAT across your MSME network?</h3>
          <p className="text-sm text-gray-300 mt-2">Register your company, onboard shop owners and start collecting high-quality feedback today.</p>
          <div className="mt-4 flex justify-center gap-3">
            <button onClick={handleRegisterCompany} className="w-1/2 h-10 px-4 py-2 border bg-yellow-700 rounded font-semibold">Register Company</button>
            <button onClick={() => setModal({ show: true, type: 'contact' })} className="w-1/2 h-10 px-4 py-2 border bg-blue-600 rounded">Talk to Sales</button>
          </div>
        </section>
      </main>

      {/* Modal Overlay */}
      {modal.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4 overflow-auto">
          <div className="bg-gray-800 text-white rounded-lg p-6 max-w-2xl w-full max-h-[80vh] overflow-auto">
            <div className="flex justify-between items-center mb-4 border-b border-gray-600 pb-2">
              <h2 className="text-xl font-semibold capitalize">{modal.type.replace(/([A-Z])/g, ' $1')}</h2>
              <button onClick={closeModal} className="text-gray-300 hover:text-white w-10 text-xl">&times;</button>
            </div>
            <div>{getDummyContent(modal.type, modal.data)}</div>
          </div>
        </div>
      )}

      <footer className="bg-black border-t mt-8">
        <div className="max-w-7xl mx-auto px-4 py-6 text-sm text-gray-600 flex flex-col md:flex-row justify-between items-center gap-4">
          <div>© {new Date().getFullYear()} Think5Whys • CSAT Module</div>
          <div className="flex gap-4">
            <button onClick={() => setModal({ show: true, type: 'privacy' })} className="hover:underline">Privacy</button>
            <button onClick={() => setModal({ show: true, type: 'terms' })} className="hover:underline">Terms</button>
            <button onClick={() => setModal({ show: true, type: 'help' })} className="hover:underline">Help</button>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CSATHome;